<template>
  <div id="app" :class="vueAppClasses">
    <router-view @setAppClasses="setAppClasses" />
  </div>
</template>
<script>
import themeConfig from '@/../themeConfig.js';
import jwt from '@/http/requests/auth/jwt/index.js';
import VersionCheck from './mixins/VersionCheck.vue';
import { loadFrill } from './assets/js/frill';
import { isFromV2Session } from './utils/platformMigrationHelper';
import {
  isActingAsStaleUser,
  reloadForSessionChange,
  takeSessionChangedNotice,
  userChangedInAnotherTab,
} from './utils/sessionChange';
export default {
  data() {
    return {
      vueAppClasses: [],
    };
  },
  mixins: [VersionCheck],
  watch: {
    '$store.state.theme'(val) {
      this.toggleClassInBody(val);
    },
    '$vs.rtl'(val) {
      document.documentElement.setAttribute('dir', val ? 'rtl' : 'ltr');
    },
  },
  methods: {
    handleBreadcrumbClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest && event.target.closest('a.vs-breadcrumb--link');
      if (!link || link.target === '_blank') return;
      if (!link.closest('.layout--main.is-shop-page')) return;
      const href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const target = url.pathname + url.search + url.hash;
      const resolved = this.$router.resolve(target);
      const matched = resolved && resolved.route && resolved.route.matched;
      if (!matched || !matched.some((record) => record.path !== '*')) return;
      event.preventDefault();
      this.$router.push(target).catch(() => {});
    },
    // Reload into the new login so this tab can't act on the previous user's clinic
    handleStorageChange(event) {
      if (userChangedInAnotherTab(event, this.$store.state.AppActiveUser)) reloadForSessionChange();
    },
    // A tab restored from the back/forward cache, or frozen in the background, missed the storage event
    handleTabResumed(event) {
      if (event.type === 'pageshow' && !event.persisted) return;
      if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') return;

      if (isActingAsStaleUser(this.$store.state.AppActiveUser)) reloadForSessionChange();
    },
    showSessionChangedNotice() {
      if (!takeSessionChangedNotice()) return;

      // The V2 shop link logs staff in as the clinic's admin, so for those name the clinic change, not a user.
      // This reads the session that took over, which is the one the tab is now using.
      const fromV2 = isFromV2Session();
      const notice = fromV2
        ? {
          title: 'Clinic switched',
          text: 'The shop was opened for another clinic in a different tab, so this tab was refreshed. Check the "Delivery to" clinic before ordering.',
        }
        : {
          title: 'Account switched',
          text: 'You signed in to a different account in another tab, so this tab was refreshed.',
        };

      this.$vs.notify({
        ...notice,
        color: 'warning',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        time: 8000,
      });
    },
    toggleClassInBody(className) {
      if (className == 'dark') {
        if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark');
        document.body.classList.add('theme-dark');
      }
      else if (className == 'semi-dark') {
        if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark');
        document.body.classList.add('theme-semi-dark');
      }
      else {
        if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark');
        if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark');
      }
    },
    setAppClasses(classesStr) {
      this.vueAppClasses.push(classesStr);
    },
    handleWindowResize() {
      this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth);

      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    },
    handleScroll() {
      this.$store.commit('UPDATE_WINDOW_SCROLL_Y', window.scrollY);
    },
    _calculateScrollbarWidth() {
      document.documentElement.style.setProperty(
        '--scrollbar-width',
        window.innerWidth - document.documentElement.clientWidth + 'px'
      );
    },
    // Phone browsers keep the URL bar over the bottom of 100dvh. Size the
    // chat frame to the visible viewport so the launcher is not sliced off.
    syncHubspotViewport() {
      const viewport = window.visualViewport;
      const height = viewport ? viewport.height : window.innerHeight;
      const top = viewport ? viewport.offsetTop : 0;
      document.documentElement.style.setProperty('--hs-vvh', Math.round(height) + 'px');
      document.documentElement.style.setProperty('--hs-vv-top', Math.round(top) + 'px');
    },
    // On a phone HubSpot does not add hs-messages-widget-open. It grows the
    // container from the ~92px launcher to the viewport when the chat opens.
    syncHubspotOpenState() {
      const el = document.getElementById('hubspot-messages-iframe-container');
      if (!el) return;
      const width = parseFloat(el.style.width);
      const height = parseFloat(el.style.height);
      el.classList.toggle('is-hubspot-open', width > 160 && height > 160);
    },
    watchHubspotWidget() {
      this.syncHubspotViewport();
      this._onHubspotViewport = () => this.syncHubspotViewport();
      window.addEventListener('resize', this._onHubspotViewport);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._onHubspotViewport);
        window.visualViewport.addEventListener('scroll', this._onHubspotViewport);
      }

      this._hubspotObserver = new MutationObserver(() => this.syncHubspotOpenState());
      const attach = () => {
        const el = document.getElementById('hubspot-messages-iframe-container');
        if (!el || el === this._hubspotEl) return;
        this._hubspotEl = el;
        this._hubspotObserver.observe(el, { attributes: true, attributeFilter: ['style'] });
        this.syncHubspotOpenState();
      };
      this._hubspotRootObserver = new MutationObserver(attach);
      if (document.body) this._hubspotRootObserver.observe(document.body, { childList: true });
      attach();
    },
  },
  mounted() {
    this.toggleClassInBody(themeConfig.theme);
    this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth);

    let vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    window.addEventListener('message', (event) => {
      if (event.data.type === 'set-localstorage') {
        Object.keys(event.data.data).forEach((key) => {
          if (typeof (event.data.data[key]) === 'object') {
            return localStorage.setItem(key, JSON.stringify(event.data.data[key]));
          }
          return localStorage.setItem(key, event.data.data[key]);
        });
      }
    }, false);

    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('pageshow', this.handleTabResumed);
    document.addEventListener('visibilitychange', this.handleTabResumed);
    document.addEventListener('click', this.handleBreadcrumbClick);
    this.showSessionChangedNotice();
    this.watchHubspotWidget();
    loadFrill();
  },
  async created() {

    jwt.init();

    let dir = this.$vs.rtl ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);

    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('scroll', this.handleScroll);

    this._calculateScrollbarWidth();

    window.addEventListener('resize', this._calculateScrollbarWidth, false);
    document.addEventListener('DOMContentLoaded', this._calculateScrollbarWidth, false);
    document.addEventListener('mousemove', this._calculateScrollbarWidth, false);
    window.addEventListener('load', this._calculateScrollbarWidth);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('pageshow', this.handleTabResumed);
    document.removeEventListener('visibilitychange', this.handleTabResumed);
    document.removeEventListener('click', this.handleBreadcrumbClick);

    window.removeEventListener('resize', this._calculateScrollbarWidth, false);
    document.removeEventListener('DOMContentLoaded', this._calculateScrollbarWidth, false);
    window.removeEventListener('load', this._calculateScrollbarWidth);

    if (this._onHubspotViewport) {
      window.removeEventListener('resize', this._onHubspotViewport);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this._onHubspotViewport);
        window.visualViewport.removeEventListener('scroll', this._onHubspotViewport);
      }
    }
    if (this._hubspotObserver) this._hubspotObserver.disconnect();
    if (this._hubspotRootObserver) this._hubspotRootObserver.disconnect();
  },
};

</script>

<style lang="scss">
@media screen and (max-width: 768px) {
  /* Closed: HubSpot paints the launcher at the corner of a full-screen iframe,
     so the iframe stays full size and is clipped to that corner.
     Open: HubSpot grows the container inline (it does not add a class). The
     is-hubspot-open class drops the clip so the whole chat is on screen. */
  body #hubspot-messages-iframe-container {
    position: fixed !important;
    z-index: 2147483647 !important;
    display: block !important;
    visibility: visible !important;
    overflow: visible !important;
    transform: none !important;
    top: var(--hs-vv-top, 0px) !important;
    right: 0 !important;
    bottom: auto !important;
    left: 0 !important;
    width: 100% !important;
    height: 100vh !important;
    height: var(--hs-vvh, 100svh) !important;
    min-width: 100% !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;

    .hs-shadow-container {
      display: none !important;
    }

    & > iframe {
      position: absolute !important;
      top: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      min-width: 100% !important;
      min-height: 100% !important;
      max-width: none !important;
      max-height: none !important;
    }

    &:not(.is-hubspot-open):not(.hs-messages-widget-open) {
      pointer-events: none !important;
      clip-path: inset(calc(100% - 120px) 0 0 calc(100% - 120px)) !important;

      & > iframe {
        pointer-events: auto !important;
      }
    }

    &.is-hubspot-open,
    &.hs-messages-widget-open {
      clip-path: none !important;
      pointer-events: auto !important;

      & > iframe {
        pointer-events: auto !important;
      }
    }
  }
}

@media screen and (min-width: 769px) {
  /* The shop frame is exactly one viewport tall. HubSpot anchors the open
     chat to the bottom edge, so the close button is clipped. */
  body #hubspot-messages-iframe-container.is-hubspot-open,
  body #hubspot-messages-iframe-container.hs-messages-widget-open {
    position: fixed !important;
    top: auto !important;
    left: auto !important;
    right: 24px !important;
    bottom: 24px !important;
    max-height: calc(100vh - 48px) !important;
    max-height: calc(100dvh - 48px) !important;
    z-index: 2147483647 !important;
    overflow: hidden !important;
  }

  body #hubspot-messages-iframe-container.is-hubspot-open > iframe,
  body #hubspot-messages-iframe-container.hs-messages-widget-open > iframe {
    max-height: calc(100vh - 48px) !important;
    max-height: calc(100dvh - 48px) !important;
  }
}
</style>


